import './DropdownToggle.scss';
import './ReactSelectDropdown.scss';
import cn from 'classnames';
import { Children, ReactElement, useMemo, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import Select, {
  ActionMeta,
  ClearIndicatorProps,
  components,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  InputActionMeta,
  InputProps,
  MenuListProps,
  MultiValue,
  OptionProps,
  SingleValue,
  ValueContainerProps
} from 'react-select';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect, { AsyncCreatableProps } from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';
import { useLabelCache } from '../../utils/hooks/useLabelCache';
import { useOnClose } from '../../utils/hooks/useOnClose';
import { Button } from '../Button';
import {
  Appearance,
  AppearanceType,
  CheckState,
  CheckStateType,
  Design,
  Placement,
  Size,
  StretchMode,
  SizeType
} from '../designConstants';
import CaretIcon from './CaretIcon.svg?react';
import {
  CHECK_STATE_ICONS,
  DropdownOption,
  DropdownSharedProps,
  DropdownToggleType,
  DropdownVariant,
  DropdownVariantType,
  MenuItem,
  SubmenuControlProps,
  SubmenuSelection,
  typeAppearance
} from './dropdown-constants';
import {
  filterSubmenuItems,
  flattenSubmenuItems,
  getSelectedSubmenuItems,
  getSubmenuCheckState,
  getSubmenuItemKey,
  matchesLabelQuery,
  toggleSubmenuItem
} from './dropdown-submenu-utils';
import { DropdownToggleShell } from './DropdownToggleShell';

interface RSOption {
  label: string;
  value: string;
  isDisabled?: boolean;
  isSubmenuChild?: boolean;
  submenuCheckState?: CheckStateType;
}

// Ride on selectProps to reach static custom components (Control, Indicators).
interface RSExtraSelectProps {
  rsAppearance: AppearanceType;
  rsSize: SizeType;
  rsClassName?: string;
  rsLeadingIcon?: ReactElement;
  rsLabel?: string | ReactElement;
  rsVariant: DropdownVariantType;
  rsOnMenuClose: () => void;
  rsSearchMouseDownRef: { current: boolean };
  rsSearchable: boolean;
  rsShowChips?: boolean;
  rsAllSelectedLabel?: string;
  rsShouldTruncateOptions: boolean;
  rsHasError?: boolean;
}

type RSSelectProps = ControlProps<RSOption, boolean, GroupBase<RSOption>>['selectProps'] &
  RSExtraSelectProps;

const RSDropdownIndicator = ({
  selectProps,
  ...props
}: DropdownIndicatorProps<RSOption, boolean, GroupBase<RSOption>>) => (
  <components.DropdownIndicator selectProps={selectProps} {...props}>
    <CaretIcon className={cn('caret-icon', { open: selectProps.menuIsOpen })} />
  </components.DropdownIndicator>
);

const RSClearIndicator = ({
  innerProps,
  ...props
}: ClearIndicatorProps<RSOption, boolean, GroupBase<RSOption>>) => (
  <components.ClearIndicator
    {...props}
    innerProps={{ ...innerProps, 'data-testid': 'dropdown-clear-icon' } as typeof innerProps}
  >
    <MdClose />
  </components.ClearIndicator>
);

const RSOptionItem = ({
  children,
  data,
  isSelected,
  isFocused,
  isDisabled,
  selectProps,
  innerRef,
  innerProps
}: OptionProps<RSOption, boolean, GroupBase<RSOption>>) => {
  const CheckboxIcon =
    CHECK_STATE_ICONS[
      data.submenuCheckState ?? (isSelected ? CheckState.CHECKED : CheckState.UNCHECKED)
    ];
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={cn('menu-row', {
        'menu-row--selected': isSelected,
        'menu-row--disabled': isDisabled,
        'menu-row--highlighted': isFocused,
        'menu-row--has-tick': selectProps.isMulti,
        'menu-row--submenu-child': data.isSubmenuChild
      })}
    >
      {selectProps.isMulti && <CheckboxIcon className='dropdown-rs__option-checkbox' />}
      <div
        className={cn('menu-row-label', {
          'menu-row-label--truncated': (selectProps as RSSelectProps).rsShouldTruncateOptions
        })}
        title={typeof children === 'string' ? children : undefined}
      >
        {children}
      </div>
    </div>
  );
};

// For multi-select, chips are collapsed into "N items selected" with a hover title listing names.
// Pass rsShowChips to render individual chip elements inside the toggle instead.
const RSValueContainer = ({
  children,
  ...props
}: ValueContainerProps<RSOption, boolean, GroupBase<RSOption>>) => {
  const childArray = Children.toArray(children);
  const input = childArray[childArray.length - 1];
  const { isMulti, value, placeholder, options } = props.selectProps;
  const { rsShowChips, rsAllSelectedLabel } = props.selectProps as RSSelectProps;

  if (isMulti) {
    const selected = Array.isArray(value) ? (value as RSOption[]) : [];
    const count = selected.length;
    const totalOptionCount = (options as RSOption[]).length;

    if (rsShowChips && count > 0) {
      // When `creatable` is on, react-select wraps `onChange` (via useCreatable) to also expect
      // an ActionMeta second argument and reads `.action` off it unconditionally — omitting it
      // (as a bare `onChange(next)` call would) throws. Build a real 'remove-value' ActionMeta so
      // this works whether or not the select is creatable.
      const handleRemove = (optValue: string) => {
        const removedValue = selected.find(o => o.value === optValue);
        if (!removedValue) return;
        const next = selected.filter(o => o.value !== optValue);
        (
          props.selectProps.onChange as (
            val: MultiValue<RSOption>,
            actionMeta: ActionMeta<RSOption>
          ) => void
        )(next, { action: 'remove-value', removedValue });
      };

      return (
        <components.ValueContainer {...props}>
          {selected.map(opt => (
            <Button
              key={opt.value}
              className='rs-chip'
              appearance={Appearance.OUTLINED}
              design={Design.NEUTRAL}
              disableAnimation
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleRemove(opt.value);
              }}
              trailingElement={<MdClose />}
            >
              {opt.label}
            </Button>
          ))}
          {input}
        </components.ValueContainer>
      );
    }

    let displayText: string | undefined;
    if (rsAllSelectedLabel && count > 0 && count === totalOptionCount) {
      displayText = rsAllSelectedLabel;
    } else if (count === 1) {
      displayText = selected[0].label;
    } else if (count > 1) {
      displayText = `${count} items selected`;
    }

    return (
      <components.ValueContainer {...props}>
        {displayText ? (
          <span className='dropdown-rs__single-value'>{displayText}</span>
        ) : (
          <span className='dropdown-rs__placeholder'>{placeholder}</span>
        )}
        {input}
      </components.ValueContainer>
    );
  }

  const singleValue = value as RSOption | null;
  return (
    <components.ValueContainer {...props}>
      {singleValue ? (
        <span className='dropdown-rs__single-value'>{singleValue.label}</span>
      ) : (
        <span className='dropdown-rs__placeholder'>{placeholder}</span>
      )}
      {input}
    </components.ValueContainer>
  );
};

// Prevents react-select from closing the menu when focus moves to the search input inside
// the MenuList. mousedown on the search input sets rsSearchMouseDownRef synchronously —
// before blur fires on the combobox — so the check here is reliable regardless of when
// document.activeElement updates.
//
// Also closes the menu itself on a genuine outside click: react-select's own `onMenuMouseDown`
// refocuses this input on every mousedown anywhere in the menu (including option rows, since
// `closeMenuOnSelect` is false for multi-select) — so once any option has been clicked, THIS is
// the input that's focused, not RSMenuList's search box, and only RSMenuList's own blur handler
// used to know how to close the menu. Without this, clicking outside would silently do nothing
// once any option had been picked.
const RSInput = ({ onBlur, ...props }: InputProps<RSOption, boolean, GroupBase<RSOption>>) => {
  const { rsSearchMouseDownRef, rsOnMenuClose } = props.selectProps as RSSelectProps;
  const handleBlur: NonNullable<typeof onBlur> = e => {
    if (rsSearchMouseDownRef.current) {
      rsSearchMouseDownRef.current = false;
      return;
    }
    const related = e.relatedTarget;
    if (!(related instanceof Element && related.closest('.dropdown-rs-container'))) {
      rsOnMenuClose();
    }
    onBlur?.(e);
  };
  return <components.Input {...props} onBlur={handleBlur} />;
};

const RSMenuList = ({
  children,
  ...props
}: MenuListProps<RSOption, boolean, GroupBase<RSOption>>) => {
  const { inputValue, onInputChange, rsOnMenuClose, rsSearchMouseDownRef, rsSearchable } =
    props.selectProps as RSSelectProps;

  if (!rsSearchable) {
    return <components.MenuList {...props}>{children}</components.MenuList>;
  }

  return (
    <components.MenuList {...props}>
      <div className='dropdown-rs__menu-search'>
        <input
          className='dropdown-rs__search-input'
          value={inputValue}
          onMouseDown={e => {
            e.stopPropagation();
            rsSearchMouseDownRef.current = true;
            // This flag is only meant to guard the ONE blur event this mousedown itself causes
            // (see the comment on RSInput). Only RSInput's blur handler ever clears it, so if
            // this search input's OWN blur fires instead (e.g. RSMenuList's blur handler closes
            // the menu directly) the flag is left stuck `true` — permanently swallowing the next
            // real outside-click blur on RSInput, whenever that next happens (which can be a
            // later, unrelated menu open — this ref lives on the component instance, not the
            // menu session). Clearing it a tick later, after any synchronous blur this mousedown
            // triggers has already run, keeps the guard scoped to just that one blur.
            setTimeout(() => {
              rsSearchMouseDownRef.current = false;
            }, 0);
          }}
          onChange={e =>
            onInputChange(e.target.value, { action: 'input-change', prevInputValue: inputValue })
          }
          onKeyDown={e => {
            if (e.key === 'Escape') {
              rsOnMenuClose();
            }
          }}
          onBlur={e => {
            const related = e.relatedTarget;
            if (!(related instanceof Element && related.closest('.dropdown-rs-container'))) {
              rsOnMenuClose();
            }
          }}
          placeholder='Search...'
        />
      </div>
      {children}
    </components.MenuList>
  );
};

const RSControl = ({
  children,
  ...props
}: ControlProps<RSOption, boolean, GroupBase<RSOption>>) => {
  const { rsAppearance, rsSize, rsClassName, rsLeadingIcon, rsLabel, rsVariant, rsHasError } =
    props.selectProps as RSSelectProps;
  const childArray = Children.toArray(children);

  return (
    <components.Control {...props}>
      {rsVariant === DropdownVariant.CELL ? (
        <div className={cn('rs-cell-control', rsSize)}>{children}</div>
      ) : (
        <DropdownToggleShell
          appearance={rsAppearance}
          size={rsSize}
          className={rsClassName}
          disabled={props.isDisabled}
          aria-expanded={props.menuIsOpen}
          leadingIcon={rsLeadingIcon}
          label={rsLabel}
          hasError={rsHasError}
          valueSlot={childArray[0]}
          indicatorsSlot={childArray[1]}
        />
      )}
    </components.Control>
  );
};

const CUSTOM_COMPONENTS = {
  Control: RSControl,
  Input: RSInput,
  DropdownIndicator: RSDropdownIndicator,
  ClearIndicator: RSClearIndicator,
  Option: RSOptionItem,
  ValueContainer: RSValueContainer,
  MenuList: RSMenuList
};

const CUSTOM_COMPONENTS_WITHOUT_CARET = { ...CUSTOM_COMPONENTS, DropdownIndicator: null };

const toRSOption = <T extends string>(opt: DropdownOption<T>): RSOption =>
  typeof opt === 'string'
    ? { label: opt, value: opt }
    : { ...opt, label: opt.label ?? opt.value, isDisabled: Boolean(opt.disabled) };

const toMenuItem = <T extends string>(opt: DropdownOption<T>): MenuItem<T> =>
  typeof opt === 'string' ? { label: opt, value: opt } : opt;

const toSubmenuRSOption = <T extends string>(
  item: MenuItem<T>,
  selection: SubmenuSelection<T>
): RSOption => ({
  label: item.label ?? item.value,
  value: getSubmenuItemKey(item),
  isDisabled: Boolean(item.disabled),
  isSubmenuChild: item.parentValue != null,
  submenuCheckState: getSubmenuCheckState(item, selection)
});

// Keys ReactSelectDropdown sets itself, or exposes under its own prop names.
type ReactSelectOwnedKeys =
  | 'unstyled'
  | 'classNamePrefix'
  | 'className'
  | 'classNames'
  | 'components'
  | 'options'
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'placeholder'
  | 'isDisabled'
  | 'isClearable'
  | 'isMulti'
  | 'isSearchable'
  | 'closeMenuOnSelect'
  | 'hideSelectedOptions'
  | 'formatOptionLabel'
  | 'menuPortalTarget'
  | 'menuIsOpen'
  | 'onMenuOpen'
  | 'onMenuClose'
  | 'inputValue'
  | 'defaultInputValue'
  | 'onInputChange'
  | 'createOptionPosition'
  | 'loadOptions'
  // Options are matched by RSOption.value / .label internally.
  | 'getOptionValue'
  | 'getOptionLabel'
  | 'isOptionSelected';

type DropdownReactSelectPassthroughProps = Omit<
  AsyncCreatableProps<RSOption, boolean, GroupBase<RSOption>>,
  ReactSelectOwnedKeys
>;

interface ReactSelectSharedProps<T extends string> extends Omit<
  DropdownSharedProps<T>,
  'overlay' | 'menuAlign'
> {
  overlay?: never;
  menuAlign?: never;
  creatable?: boolean;
  createOptionPosition?: 'first' | 'last';
  optionFetcher?: (input: string) => Promise<DropdownOption<T>[]>;
  variant?: DropdownVariantType;
  // Widens the menu past the toggle so every option label fits on one line.
  fitMenuToContent?: boolean;
  reactSelectProps?: DropdownReactSelectPassthroughProps;
  reactBootstrapProps?: never;
}

export interface ReactSelectSingleProps<T extends string> extends ReactSelectSharedProps<T> {
  searchable: true;
  multiSelect?: false;
  value?: T | null;
  onChange: (value: T | null) => void;
}

export interface ReactSelectMultiProps<T extends string> extends ReactSelectSharedProps<T> {
  searchable?: boolean;
  multiSelect: true;
  showChips?: boolean;
  // Overrides the "N items selected" summary with this label once every option is selected (e.g.
  // "All Segment Types"). No effect when `showChips` is on.
  allSelectedLabel?: string;
  value?: T[];
  onChange: (value: T[]) => void;
}

// Options with `children` render as an inline, one-tier submenu.
export interface ReactSelectSubmenuProps<T extends string>
  extends ReactSelectSharedProps<T>, SubmenuControlProps<T> {
  searchable: true;
  multiSelect: true;
  creatable?: never;
  optionFetcher?: never;
}

export interface ReactSelectCellSingleProps<T extends string> extends Omit<
  ReactSelectSingleProps<T>,
  'searchable' | 'variant'
> {
  variant: typeof DropdownVariant.CELL;
  searchable?: boolean;
}

interface ReactSelectDropdownProps<T extends string>
  extends Omit<ReactSelectSharedProps<T>, 'reactBootstrapProps'>, Partial<SubmenuControlProps<T>> {
  multiSelect?: boolean;
  // When false, the in-menu search input is hidden and react-select's combobox input is readonly.
  searchable?: boolean;
  showChips?: boolean;
  // Multi-select only: overrides the "N items selected" summary with this label once every option
  // is selected (e.g. "All Segment Types"). No effect when `showChips` is on.
  allSelectedLabel?: string;
  value?: T | T[] | null;
  onChange: (value: T | T[] | null) => void;
}

export const ReactSelectDropdown = <T extends string>({
  type = DropdownToggleType.TEXT_INPUT,
  size = Size.LG,
  label,
  ariaLabel,
  leadingIcon,
  placeholder,
  className,
  disabled,
  clearable,
  onClear,
  hideCaret = false,
  creatable,
  createOptionPosition,
  variant = DropdownVariant.DEFAULT,
  options = [],
  optionFetcher,
  multiSelect,
  searchable = true,
  showChips,
  allSelectedLabel,
  formatOptionLabel,
  menuPortalTarget,
  fitMenuToContent = false,
  onMenuClose,
  stretch = StretchMode.FILL,
  shouldTruncateOptions = false,
  hasError,
  reactSelectProps,
  value,
  onChange,
  submenuValue,
  onSubmenuChange
}: ReactSelectDropdownProps<T>): ReactElement => {
  const [isMenuOpen, setIsMenuOpen] = useState(reactSelectProps?.defaultMenuIsOpen ?? false);
  useOnClose(isMenuOpen, onMenuClose);
  const closeMenu = (): void => {
    setIsMenuOpen(false);
    setSearchValue('');
  };
  const [searchValue, setSearchValue] = useState('');
  const searchMouseDownRef = useRef(false);

  const rsOptions = useMemo(() => options.map(toRSOption), [options]);

  // Append-only label cache so selected values keep their friendly label even
  // after the parent filters them out of `options` (e.g. multi-selects that
  // hide already-picked items from the menu).
  const labelCache = useLabelCache(
    rsOptions,
    o => o.value,
    o => o.label
  );

  const resolveRSOption = (v: string): RSOption => {
    const found = rsOptions.find(o => o.value === v);
    if (found) return found;
    const cached = labelCache.get(v);
    return cached != null ? { label: cached, value: v } : { label: v, value: v };
  };

  const rsValue = useMemo(() => {
    if (value == null) return multiSelect ? [] : null;
    if (multiSelect) {
      const valueArr = Array.isArray(value) ? value : [value];
      return valueArr.map(resolveRSOption);
    }
    const v = Array.isArray(value) ? value[0] : value;
    if (!v) return null;
    return resolveRSOption(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, multiSelect, rsOptions, labelCache]);

  const isSubmenu = submenuValue != null;
  const submenuItems = useMemo(
    () => (isSubmenu ? options.map(toMenuItem) : null),
    [isSubmenu, options]
  );
  const submenuItemsByKey = useMemo(
    () =>
      new Map(flattenSubmenuItems(submenuItems ?? []).map(item => [getSubmenuItemKey(item), item])),
    [submenuItems]
  );
  const resolvedFormatOptionLabel = useMemo(() => {
    if (!formatOptionLabel || !isSubmenu) return formatOptionLabel;
    return (option: RSOption, meta: { context: 'menu' | 'value' }) => {
      const item = submenuItemsByKey.get(option.value);
      return formatOptionLabel(
        item ? { label: option.label, value: String(item.value) } : option,
        meta
      );
    };
  }, [formatOptionLabel, isSubmenu, submenuItemsByKey]);
  const submenuSelectedOptions = useMemo(() => {
    if (!submenuItems || !submenuValue) return null;
    return getSelectedSubmenuItems(submenuItems, submenuValue).map(item =>
      toSubmenuRSOption(item, submenuValue)
    );
  }, [submenuItems, submenuValue]);
  const submenuVisibleOptions = useMemo(() => {
    if (!submenuItems || !submenuValue) return null;
    return flattenSubmenuItems(
      filterSubmenuItems(submenuItems, item => matchesLabelQuery(item, searchValue))
    ).map(item => toSubmenuRSOption(item, submenuValue));
  }, [submenuItems, submenuValue, searchValue]);
  const effectiveValue = submenuSelectedOptions ?? rsValue;

  const hoverTitle = useMemo(() => {
    if (!multiSelect || showChips) return undefined;
    const selected = Array.isArray(effectiveValue) ? effectiveValue : [];
    if (selected.length <= 1) return undefined;
    return [...selected]
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(v => v.label)
      .join(', ');
  }, [multiSelect, showChips, effectiveValue]);

  const handleChange = (
    newValue: MultiValue<RSOption> | SingleValue<RSOption>,
    actionMeta: ActionMeta<RSOption>
  ) => {
    if (actionMeta.action === 'clear') {
      onClear?.();
    }
    if (submenuValue != null) {
      if (actionMeta.action === 'clear') {
        onSubmenuChange?.({});
        return;
      }
      const toggledOption = actionMeta.option ?? actionMeta.removedValue;
      const toggledItem = toggledOption && submenuItemsByKey.get(toggledOption.value);
      if (toggledItem) {
        onSubmenuChange?.(toggleSubmenuItem(toggledItem, submenuValue));
      }
      return;
    }
    if (newValue == null) {
      onChange(multiSelect ? ([] as T[]) : null);
      return;
    }
    if (multiSelect) {
      onChange((newValue as MultiValue<RSOption>).map(o => o.value as T));
    } else {
      onChange((newValue as RSOption).value as T);
    }
  };

  const loadOptions = useMemo(
    () =>
      optionFetcher
        ? (input: string) => optionFetcher(input).then(opts => opts.map(toRSOption))
        : undefined,
    [optionFetcher]
  );

  const resolvedCreateOptionPosition =
    createOptionPosition ?? (variant === DropdownVariant.CELL ? 'first' : undefined);

  // CELL variant lives inside scrollable tables; portal the menu to <body> so it
  // overlays the table instead of clipping/forcing scroll on the parent.
  const isCellVariant = variant === DropdownVariant.CELL;
  const {
    menuPlacement = 'auto',
    // 'fixed' measures placement against the viewport instead of the nearest scroll container.
    menuPosition = isCellVariant ? 'fixed' : undefined,
    'aria-label': selectAriaLabel = ariaLabel,
    ...rsPassthroughProps
  } = reactSelectProps ?? {};

  const commonProps = {
    ...rsPassthroughProps,
    menuPlacement,
    menuPosition,
    'aria-label': selectAriaLabel,
    unstyled: true,
    classNamePrefix: 'dropdown-rs',
    className: cn('dropdown-rs-container', stretch, {
      'dropdown-rs-cell': isCellVariant,
      'dropdown-rs-chips': showChips,
      'dropdown-rs-md': size === Size.MD
    }),
    classNames: {
      menu: () => cn({ 'fit-content': fitMenuToContent })
    },
    components: hideCaret ? CUSTOM_COMPONENTS_WITHOUT_CARET : CUSTOM_COMPONENTS,
    options: submenuVisibleOptions ?? rsOptions,
    value: effectiveValue,
    // Submenu options are pre-filtered so a matching child keeps its parent visible.
    ...(submenuVisibleOptions ? { filterOption: null } : {}),
    onChange: handleChange,
    placeholder: placeholder ?? '',
    isDisabled: disabled,
    isClearable: clearable,
    isMulti: multiSelect,
    createOptionPosition: resolvedCreateOptionPosition,
    formatOptionLabel: resolvedFormatOptionLabel,
    closeMenuOnSelect: !multiSelect,
    hideSelectedOptions: false,
    isSearchable: searchable,
    menuPortalTarget:
      menuPortalTarget ??
      (isCellVariant && typeof document !== 'undefined' ? document.body : undefined),
    inputValue: searchValue,
    onInputChange: (val: string, meta: InputActionMeta) => {
      if (meta.action === 'input-change') {
        setSearchValue(val);
      }
      // Keeps the search text after a pick so an async multi-select keeps its fetched options.
      if (meta.action === 'set-value' && multiSelect) return searchValue;
      return val;
    },
    menuIsOpen: isMenuOpen,
    onMenuOpen: () => {
      setIsMenuOpen(true);
      setSearchValue('');
    },
    onMenuClose: closeMenu,
    // `satisfies` catches typos here — the cast in RSControl
    // (`props.selectProps as RSSelectProps`) would otherwise coerce a
    // mistyped field to undefined at read time.
    ...({
      rsAppearance: typeAppearance[type],
      rsSize: size,
      rsClassName: className,
      rsLeadingIcon: leadingIcon,
      rsLabel: label,
      rsVariant: variant,
      rsShowChips: showChips,
      rsAllSelectedLabel: allSelectedLabel,
      rsOnMenuClose: closeMenu,
      rsSearchMouseDownRef: searchMouseDownRef,
      rsSearchable: searchable,
      rsShouldTruncateOptions: shouldTruncateOptions,
      rsHasError: hasError
    } satisfies RSExtraSelectProps)
  };

  let selectEl: ReactElement;
  if (loadOptions && creatable) {
    selectEl = (
      <AsyncCreatableSelect<RSOption, boolean, GroupBase<RSOption>>
        {...commonProps}
        loadOptions={loadOptions}
      />
    );
  } else if (loadOptions) {
    selectEl = (
      <AsyncSelect<RSOption, boolean, GroupBase<RSOption>>
        {...commonProps}
        loadOptions={loadOptions}
      />
    );
  } else if (creatable) {
    selectEl = <CreatableSelect<RSOption, boolean, GroupBase<RSOption>> {...commonProps} />;
  } else {
    selectEl = <Select<RSOption, boolean, GroupBase<RSOption>> {...commonProps} />;
  }

  if (!hoverTitle) {
    return <div className={cn('dropdown-rs-tooltip-wrapper', stretch)}>{selectEl}</div>;
  }

  // Let OverlayTrigger's default hover/focus trigger drive visibility, but
  // suppress the tooltip while the menu is open by tagging the portaled
  // Tooltip element with `.hidden` (display: none — see ReactSelectDropdown.scss).
  return (
    <OverlayTrigger
      placement={Placement.BOTTOM}
      overlay={
        <Tooltip className={cn('dropdown-rs-hover-tooltip', { hidden: isMenuOpen })}>
          {hoverTitle}
        </Tooltip>
      }
    >
      <div className={cn('dropdown-rs-tooltip-wrapper', stretch)}>{selectEl}</div>
    </OverlayTrigger>
  );
};
